/**
 * useOracleSearchStream
 *
 * Streaming hook for Search and Discovery that uses the `oracleSearchStream`
 * GraphQL subscription. Provides a state machine (IDLE → LOADING → STREAMING
 * → RESULTS / ERROR) and accumulates streamed text chunks with inline
 * refinement tags.
 *
 * Supports configurable result types (`content`, `playlist`, `profile`).
 * When full result data is returned inline on the final payload, it is used
 * directly. For minimal playlist results (ID-only), a follow-up query via
 * `GET_PLAYLISTS_WITH_ITEMS` resolves full data.
 */

import {
  useState, useCallback, useRef, useEffect,
} from 'react'
import { gql, useApolloClient } from '@apollo/client'
import type { Observable, FetchResult } from '@apollo/client'
import { APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY } from '../../apollo-graphql/index'
import type { PlaylistContentItem } from '../types'
import type {
  OracleSearchResults,
  OraclePlaylistResult,
  OracleContentResult,
  OracleContentEntity,
  OracleSearchType,
} from './useOracleSearch'
import { EMPTY_RESULTS, DEFAULT_TYPES } from './oracle-search-constants'
import { GET_PLAYLISTS_WITH_ITEMS } from '../query'
import { PLAYLIST_ITEM_CONTENT_FIELDS } from '../fragments'

// ── Text normalisation ──────────────────────────────────────────────────

/**
 * Converts bracket-style refinement tags (`[refinement text="..."]`) from the
 * backend into `<Gaia type="refine" text="..." />` so downstream parsers
 * (discover-utils / gaiaTagParser) handle them uniformly.
 */
const BRACKET_REFINEMENT_REGEX = /\[refinement\s+text="([^"]*)"\]/g

/**
 * Strips `[CONTENT_ORDER]...[/CONTENT_ORDER]` and
 * `[PLAYLIST_ORDER]...[/PLAYLIST_ORDER]` metadata tags that the backend
 * embeds in the narrative for ordering purposes. These should never be
 * shown to users.
 */
const ORDER_TAG_REGEX = /\[(?:CONTENT_ORDER|PLAYLIST_ORDER)\][\s\S]*?\[\/(?:CONTENT_ORDER|PLAYLIST_ORDER)\]\s*/g

/**
 * Removes any trailing incomplete tag from streamed text so partial tags
 * don't leak into the UI while chunks are still arriving.
 * Matches:
 *   - `<Gaia...` (no closing `>`)
 *   - `[refinement...` (no closing `]`)
 *   - `[CONTENT_ORDER]...` or `[PLAYLIST_ORDER]...` (no closing tag yet)
 */
const TRAILING_INCOMPLETE_TAG_REGEX = /(?:<Gaia[^>]*|\[refinement[^\]]*|\[(?:CONTENT_ORDER|PLAYLIST_ORDER)\][\s\S]*?)$/

export function normalizeBracketTags (text: string): string {
  // Fast path: skip regex if no brackets or angle brackets present
  if (!text.includes('[') && !text.includes('<Gaia')) return text
  return text
    .replace(TRAILING_INCOMPLETE_TAG_REGEX, '')
    .replace(ORDER_TAG_REGEX, '')
    .replace(
      BRACKET_REFINEMENT_REGEX,
      (_match, value: string) => `<Gaia type="refine" text="${value}" />`,
    )
}

// ── GraphQL documents ───────────────────────────────────────────────────

const ORACLE_SEARCH_FEEDBACK_MUTATION = gql`
  mutation OracleSearchFeedback($input: OracleSearchFeedbackInput!) {
    oracleSearchFeedback(input: $input) {
      success
      errors
      thumbsValue
    }
  }
`

const ORACLE_SEARCH_STREAM_SUBSCRIPTION = gql`
  ${PLAYLIST_ITEM_CONTENT_FIELDS}
  subscription OracleSearchStream($input: OracleSearchStreamInput!, $language: [String]) {
    oracleSearchStream(input: $input) {
      chatSessionId
      turnId
      textChunk
      statusText
      isFinal
      success
      errors
      refinementSuggestions

      contentResults {
        contentId
        contentType
        title
        content {
          id
          title
          path(format: RELATIVE)
          contentType
          contentId
          shortDescription
          episodeNumber
          seasonNumber
          instructor
          isNew
          displayType
          subType
          studio
          contentAnalytic {
            votesUpCountTotal
            votesCountTotal
            commentsCountTotal
          }
          featureMedia {
            duration
            mediaId
            userMediaPosition {
              position
            }
            contentPolicy {
              accessCheck {
                allowed
              }
            }
          }
          contentAssetReferences(
            contentAssetReferences: [{ type: "keyart-no-text" }]
            limit: 1
          ) {
            asset {
              renderUrl
              width
              height
            }
          }
          imageContentRelationships(
            types: ["d6-keyart-with-text", "d6-keyart-no-text", "d6-poster-image"]
            language: $language
          ) {
            type
            image {
              url
            }
          }
          references(typesByTypeTag: true) {
            contentReferenceType { type }
            contentTo(language: $language) {
              title
              path(format: RELATIVE)
              contentType
              contentId
              language
            }
          }
          contentSeries(language: $language) {
            title
            path(format: RELATIVE)
            contentId
          }
        }
      }

      playlistResults {
        id
        title
        playlist {
          id
          playlistName
          itemCount
          followerCount
          userProfile {
            uuid
            displayName
          }
          playlistItems {
            ...PlaylistItemContentFields
          }
        }
      }
    }
  }
`

// ── Types ────────────────────────────────────────────────────────────

interface StreamContentResult {
  contentId: number | string
  contentType: string
  title: string
  content: OracleContentEntity | null
}

interface StreamPlaylistResult {
  id: string
  title: string
  score: number
  playlist: {
    id: string
    playlistName?: string
    itemCount?: number
    followerCount?: number
    userProfile?: {
      uuid: string
      displayName?: string | null
    } | null
    playlistItems?: Array<{
      id: string
      contentId: string
      content?: Array<{
        id: string
        title: string
        path: string
        contentType: string
        contentId: number | string
        featureMedia?: {
          duration: number
        } | null
        contentAssetReferences?: Array<{
          asset: { renderUrl: string }
        }> | null
        imageContentRelationships?: Array<{
          type: string
          image: { url: string }
        }> | null
      }> | null
    }>
  } | null
}

interface OracleSearchStreamPayload {
  oracleSearchStream: {
    chatSessionId: string | null
    turnId: string | null
    textChunk: string | null
    statusText: string | null
    contentResults: StreamContentResult[] | null
    playlistResults: StreamPlaylistResult[] | null
    refinementSuggestions: string[] | null
    success: boolean
    errors: string[] | null
    isFinal: boolean
  }
}

interface OracleSearchStreamInput {
  query: string
  types: string[]
  language: string
  limit: number
  chatSessionId?: string | null
  personalityId?: number | null
}

export type DiscoverStreamState = 'IDLE' | 'LOADING' | 'STREAMING' | 'RESULTS' | 'ERROR'

export interface UseOracleSearchStreamReturn {
  search: (query: string) => void
  refine: (text: string) => void
  reset: () => void
  sendFeedback: (thumbsValue: 'up' | 'down' | 'none') => Promise<void>
  state: DiscoverStreamState
  statusKey: string | null
  streamingText: string
  results: OracleSearchResults
  refinementSuggestions: string[]
  error: string | null
  chatSessionId: string | null
  turnId: string | null
}

// ── Playlist data resolution types (fallback for ID-only results) ────

interface PlaylistsWithItemsData {
  playlistList: {
    playlists: Array<{
      id: string
      playlistName: string
      playlistType: string
      visibility: 'PUBLIC' | 'ANONYMOUS' | 'PRIVATE'
      itemCount: number
      playlistItems: Array<{
        id: string
        contentId: string
        contentType: string
        playlistPosition: number
        content: PlaylistContentItem[] | null
      }>
    }>
  }
}

// ── Result mapping helpers ──────────────────────────────────────────

/**
 * Checks whether a playlist result has full inline data (playlistName present)
 * or only a bare ID that needs resolution via GET_PLAYLISTS_WITH_ITEMS.
 */
export function hasInlinePlaylistData (result: StreamPlaylistResult): boolean {
  return !!result.playlist?.playlistName
}

/**
 * Maps a stream playlist result with full inline data to OraclePlaylistResult.
 */
export function mapInlinePlaylistResult (sr: StreamPlaylistResult): OraclePlaylistResult {
  const pl = sr.playlist
  return {
    id: sr.id,
    title: sr.title,
    score: 0,
    playlist: pl ? {
      id: pl.id,
      playlistName: pl.playlistName || '',
      itemCount: pl.itemCount,
      followerCount: pl.followerCount,
      userProfile: pl.userProfile ? {
        uuid: pl.userProfile.uuid,
        displayName: pl.userProfile.displayName,
      } : null,
      playlistItems: (pl.playlistItems || []).map((item) => ({
        id: item.id,
        contentId: item.contentId,
        contentType: '',
        content: item.content || null,
      })),
    } : null,
  }
}

/**
 * Maps stream content results directly to OracleContentResult[].
 */
export function mapContentResults (
  streamResults: StreamContentResult[],
): OracleContentResult[] {
  return streamResults
    .filter((r) => r.content)
    .map((r) => ({
      contentId: r.contentId,
      brooklynId: '',
      contentType: r.contentType,
      title: r.title,
      score: 0,
      content: r.content,
    }))
}

// ── Hook ─────────────────────────────────────────────────────────────

export default function useOracleSearchStream (
  jwt: string | null,
  language: string,
  types: OracleSearchType[] = DEFAULT_TYPES,
): UseOracleSearchStreamReturn {
  const apolloClient = useApolloClient()

  const [state, setState] = useState<DiscoverStreamState>('IDLE')
  const [statusKey, setStatusKey] = useState<string | null>(null)
  const [streamingText, setStreamingText] = useState('')
  const [results, setResults] = useState<OracleSearchResults>(EMPTY_RESULTS)
  const [refinementSuggestions, setRefinementSuggestions] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [chatSessionId, setChatSessionId] = useState<string | null>(null)
  const [turnId, setTurnId] = useState<string | null>(null)

  // Stable refs for values that change but shouldn't recreate callbacks
  const jwtRef = useRef(jwt)
  const languageRef = useRef(language)
  const typesRef = useRef(types)
  jwtRef.current = jwt
  languageRef.current = language
  typesRef.current = types

  // Mirror state in a ref so the subscription callback can read it
  // without triggering a new closure on every setState.
  const stateRef = useRef<DiscoverStreamState>(state)
  stateRef.current = state

  // Track active subscription for cleanup
  type Subscription = { unsubscribe: () => void }
  const subscriptionRef = useRef<Subscription | null>(null)
  const requestIdRef = useRef(0)
  const originalQueryRef = useRef('')

  // Serializes mid-stream resolve + write so the final branch can await
  // it before issuing its own fetch. Without this, a slow mid-stream
  // apolloClient.query that resolves AFTER the final query's .then() fires
  // overwrites the complete final dataset with the smaller mid slice.
  // Reset to null at the start of every executeSearch.
  const midResolvePromiseRef = useRef<Promise<void> | null>(null)

  /**
   * Resolves full playlist data from playlist IDs returned by the subscription.
   * Used as a fallback when the subscription returns minimal playlist data
   * (ID-only, as in the Discovery tab's playlist-only mode).
   */
  const resolvePlaylistData = useCallback(async (
    streamResults: StreamPlaylistResult[],
    requestId: number,
  ): Promise<OraclePlaylistResult[]> => {
    const playlistIds = streamResults
      .map((r) => r.playlist?.id)
      .filter((id): id is string => !!id)

    if (playlistIds.length === 0) return []

    const { data } = await apolloClient.query<PlaylistsWithItemsData>({
      query: GET_PLAYLISTS_WITH_ITEMS,
      variables: { playlistIds, language: [languageRef.current, 'en', '_primary'] },
      fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
      context: { jwt: jwtRef.current },
    })

    // Guard against stale responses
    if (requestIdRef.current !== requestId) return []

    const playlistMap = new Map<string, PlaylistsWithItemsData['playlistList']['playlists'][number]>()
    if (data?.playlistList?.playlists) {
      data.playlistList.playlists.forEach((p) => {
        playlistMap.set(p.id, p)
      })
    }

    return streamResults.map((sr) => {
      const fullPlaylist = sr.playlist?.id ? playlistMap.get(sr.playlist.id) : null
      return {
        id: sr.id,
        title: sr.title,
        score: sr.score,
        playlist: fullPlaylist ? {
          id: fullPlaylist.id,
          playlistName: fullPlaylist.playlistName,
          visibility: fullPlaylist.visibility,
          itemCount: fullPlaylist.itemCount,
          playlistItems: fullPlaylist.playlistItems.map((item) => ({
            id: item.id,
            contentId: item.contentId,
            contentType: item.contentType,
            playlistPosition: item.playlistPosition,
            content: item.content,
          })),
        } : null,
      }
    })
  }, [apolloClient])

  /**
   * Executes a streaming search or refinement.
   */
  const executeSearch = useCallback((query: string, chatSession?: string | null) => {
    // Teardown previous subscription
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe()
      subscriptionRef.current = null
    }

    requestIdRef.current += 1
    const currentRequestId = requestIdRef.current
    // New request — any prior mid-resolve promise is moot.
    midResolvePromiseRef.current = null

    setState('LOADING')
    setStatusKey('searching')
    setStreamingText('')
    setResults(EMPTY_RESULTS)
    setError(null)
    setRefinementSuggestions([])
    setTurnId(null)

    // Build subscription input
    const input: OracleSearchStreamInput = {
      query,
      types: typesRef.current,
      language: languageRef.current,
      limit: 16,
      personalityId: 2, // Sage personality
    }
    if (chatSession) {
      input.chatSessionId = chatSession
    }

    // Accumulated text across chunks
    let accumulated = ''
    const observable: Observable<FetchResult<OracleSearchStreamPayload>> = apolloClient.subscribe({
      query: ORACLE_SEARCH_STREAM_SUBSCRIPTION,
      variables: { input, language: [languageRef.current, 'en', '_primary'] },
      context: { jwt: jwtRef.current },
    })

    const subscription = observable.subscribe({
      next: (response) => {
        // Guard stale
        if (requestIdRef.current !== currentRequestId) return

        const payload = response.data?.oracleSearchStream
        if (!payload) return

        // Handle status updates from backend (e.g. "searching", "generating")
        if (payload.statusText) {
          setStatusKey(payload.statusText)
        }

        // Accumulate text chunks (non-final payloads)
        if (payload.textChunk && !payload.isFinal) {
          // Clear status text — real narrative is replacing it
          setStatusKey(null)
          accumulated += payload.textChunk
          const normalized = normalizeBracketTags(accumulated)
          // Don't blank the UI when normalization strips an in-progress tag
          if (normalized.length > 0) {
            setStreamingText(normalized)
          }
          // else: keep showing prevNormalized — tag is still being received
          if (stateRef.current !== 'STREAMING') {
            setState('STREAMING')
          }
        }

        // Store session info as it arrives
        if (payload.chatSessionId) {
          setChatSessionId(payload.chatSessionId)
        }
        if (payload.turnId) {
          setTurnId(payload.turnId)
        }

        // Handle mid-stream results (direct search path emits results
        // before the overview text so tiles appear immediately)
        if (!payload.isFinal && (payload.contentResults || payload.playlistResults)) {
          const rawContent = payload.contentResults || []
          const content = mapContentResults(rawContent)

          const streamPlaylists = payload.playlistResults || []
          if (streamPlaylists.length > 0) {
            if (hasInlinePlaylistData(streamPlaylists[0])) {
              const playlists = streamPlaylists.map(mapInlinePlaylistResult)
              setResults({ playlists, content, profiles: [] })
            } else {
              // Store the in-flight fetch-and-write as a single awaitable
              // so the final branch can serialize behind it. Prior refs
              // are superseded — only the latest mid resolve matters for
              // ordering the final write.
              midResolvePromiseRef.current = (async () => {
                try {
                  const resolved = await resolvePlaylistData(streamPlaylists, currentRequestId)
                  if (requestIdRef.current !== currentRequestId) return
                  setResults({ playlists: resolved, content, profiles: [] })
                } catch {
                  // Non-fatal: tiles may lack full playlist data but
                  // the final payload will retry resolution anyway.
                  if (requestIdRef.current !== currentRequestId) return
                  setResults({ playlists: [], content, profiles: [] })
                }
              })()
            }
          } else {
            setResults({ playlists: [], content, profiles: [] })
          }
        }

        // Handle final event
        if (payload.isFinal) {
          if (!payload.success) {
            const errorMsg = payload.errors?.join(', ') || 'Search failed'
            setState('ERROR')
            setError(errorMsg)
            return
          }

          // Replace streaming text with final clean version
          if (payload.textChunk) {
            setStreamingText(normalizeBracketTags(payload.textChunk))
          }

          // Store refinement suggestions
          if (payload.refinementSuggestions && payload.refinementSuggestions.length > 0) {
            setRefinementSuggestions(payload.refinementSuggestions)
          }

          // Map content and playlist results if present on the final payload.
          // In the direct search path, results arrive mid-stream (above) and
          // the final payload only carries text + refinements — skip overwriting.
          const hasResultsOnFinal = (payload.contentResults && payload.contentResults.length > 0)
            || (payload.playlistResults && payload.playlistResults.length > 0)

          if (hasResultsOnFinal) {
            const rawContent = payload.contentResults || []
            const content = mapContentResults(rawContent)

            const streamPlaylists = payload.playlistResults || []
            if (streamPlaylists.length > 0) {
              if (hasInlinePlaylistData(streamPlaylists[0])) {
                // Serialize behind any in-flight mid-stream resolve so
                // a slow mid can never overwrite the final inline data.
                const midInFlight = midResolvePromiseRef.current;
                (async () => {
                  try {
                    if (midInFlight) await midInFlight
                  } catch { /* mid failure is non-fatal */ }
                  if (requestIdRef.current !== currentRequestId) return
                  const playlists = streamPlaylists.map(mapInlinePlaylistResult)
                  setResults({ playlists, content, profiles: [] })
                  setState('RESULTS')
                })()
              } else {
                // Serialize behind any in-flight mid-stream resolve so the
                // smaller mid-slice can never overwrite the final dataset
                // (the race happens when mid's query resolves AFTER final's
                // .then fires — observable under Slow 3G throttling).
                const midInFlight = midResolvePromiseRef.current;
                (async () => {
                  try {
                    if (midInFlight) {
                      await midInFlight
                    }
                    if (requestIdRef.current !== currentRequestId) return
                    const resolved = await resolvePlaylistData(streamPlaylists, currentRequestId)
                    if (requestIdRef.current !== currentRequestId) return
                    setResults({ playlists: resolved, content, profiles: [] })
                    setState('RESULTS')
                  } catch (catchErr: unknown) {
                    if (requestIdRef.current !== currentRequestId) return
                    const msg = catchErr instanceof Error ? catchErr.message : 'Failed to load playlist details'
                    setError(msg)
                    setState('ERROR')
                  }
                })()
              }
            } else {
              setResults({ playlists: [], content, profiles: [] })
              setState('RESULTS')
            }
          } else {
            // No results on final payload — results were already set
            // mid-stream (direct search path). Just transition to RESULTS.
            setState('RESULTS')
          }
        }
      },
      error: (err: unknown) => {
        if (requestIdRef.current !== currentRequestId) return
        const msg = err instanceof Error ? err.message : 'Stream connection failed'
        setState('ERROR')
        setStatusKey(null)
        setError(msg)
      },
    })

    subscriptionRef.current = subscription
  }, [apolloClient, resolvePlaylistData])

  const search = useCallback((query: string) => {
    originalQueryRef.current = query
    setChatSessionId(null)
    setTurnId(null)
    executeSearch(query)
  }, [executeSearch])

  const refine = useCallback((text: string) => {
    // Refinement passes the text as the query + chatSessionId for context
    executeSearch(text, chatSessionId)
  }, [executeSearch, chatSessionId])

  const reset = useCallback(() => {
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe()
      subscriptionRef.current = null
    }
    requestIdRef.current += 1
    setState('IDLE')
    setStatusKey(null)
    setStreamingText('')
    setResults(EMPTY_RESULTS)
    setRefinementSuggestions([])
    setError(null)
    setChatSessionId(null)
    setTurnId(null)
  }, [])

  const sendFeedback = useCallback(async (thumbsValue: 'up' | 'down' | 'none') => {
    if (!turnId) return
    try {
      await apolloClient.mutate<{
        oracleSearchFeedback: {
          success: boolean
          errors: string[] | null
          thumbsValue: string
        }
      }>({
        mutation: ORACLE_SEARCH_FEEDBACK_MUTATION,
        variables: { input: { turnId, thumbsValue } },
        context: { jwt: jwtRef.current },
      })
    } catch {
      // non-fatal: feedback is fire-and-forget
    }
  }, [turnId, apolloClient])

  // Cleanup subscription on unmount to prevent memory leaks and
  // setState-on-unmounted-component warnings.
  useEffect(() => {
    return () => {
      // Bump request ID so in-flight IIFEs (mid-stream resolve, final
      // resolve) bail out via their requestIdRef guards on unmount.
      requestIdRef.current += 1
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
        subscriptionRef.current = null
      }
    }
  }, [])

  return {
    search,
    refine,
    reset,
    sendFeedback,
    state,
    statusKey,
    streamingText,
    results,
    refinementSuggestions,
    error,
    chatSessionId,
    turnId,
  }
}
